import { cn } from "@/lib/utils"
import type { MessageContent } from "@/types/workflow"
import { Bell, Link } from "lucide-react"

interface PushNotificationPreviewProps {
  contents: MessageContent[]
  /** Site/app name shown as context */
  siteName?: string
}

/** Renders message content as a browser-style push notification (horizontal toast). */
export function PushNotificationPreview({ contents, siteName = "Your site" }: PushNotificationPreviewProps) {
  if (contents.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[72px] rounded-lg border border-dashed bg-muted/30 p-4">
        <p className="text-sm text-muted-foreground">Sem conteúdo para visualizar</p>
      </div>
    )
  }

  // Flatten to title, body, image, link for push-style display
  let title = ""
  let body = ""
  let imageUrl: string | undefined
  let linkLabel: string | undefined

  for (const c of contents) {
    if (c.type === "text" && c.text) {
      const parts = c.text.split("\n\n").filter(Boolean)
      if (parts.length > 1 && !title) title = parts[0]
      if (!body) body = parts.length > 1 ? parts.slice(1).join("\n\n") : (parts[0] ?? "")
      if (c.buttons?.[0]) linkLabel = c.buttons[0].title
    } else if (c.type === "card") {
      if (c.title) title = c.title
      if (c.subtitle) body = c.subtitle
      if (c.imageUrl) imageUrl = c.imageUrl
      if (c.buttons?.[0]) linkLabel = c.buttons[0].title
    } else if (c.type === "image" && c.imageUrl) {
      imageUrl = c.imageUrl
    }
  }

  if (!title && !body) body = "Notification"

  return (
    <div
      className={cn(
        "flex items-stretch gap-3 rounded-xl border bg-white p-3 shadow-lg",
        "max-w-md overflow-hidden"
      )}
    >
      {/* Icon */}
      <div className="flex shrink-0 items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary">
        <Bell className="h-5 w-5" />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1 flex flex-col justify-center gap-0.5">
        <p className="text-[10px] text-muted-foreground truncate">{siteName}</p>
        {title && (
          <p className="font-semibold text-sm text-foreground truncate">{title}</p>
        )}
        <p className="text-xs text-muted-foreground line-clamp-2">{body}</p>
        {linkLabel && (
          <div className="mt-1 flex items-center gap-1 text-xs text-primary">
            <Link className="h-3 w-3 shrink-0" />
            <span>{linkLabel}</span>
          </div>
        )}
      </div>

      {/* Thumbnail */}
      {imageUrl && (
        <div className="shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-muted">
          <img
            src={imageUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      )}
    </div>
  )
}
